import {
  Component,
  EventEmitter,
  Output,
  Input,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatRadioModule } from '@angular/material/radio';
import { QuillEditorComponent } from 'ngx-quill';
import { EmailPreviewComponent } from '../../../email-preview/email-preview.component';
import { ApiService } from '../../../../services/api.service';
import { NotificationService } from '../../../../services/notification.service'; // NotificationService;
import { EmailPreviewService } from '../../../../services/email-preview.service';
import { Router } from '@angular/router';
import { PhoneValidator } from '../../../../shared/validators/phone.validator';

const DEFAULT_EMAIL_VALIDATION_ERRORS = {
  subject: '',
  fromEmail: '',
  logo: '',
  image: '',
  header: '',
  bodyText: '',
  body2Text: '',
  body3Text: '',
  body4Text: '',
  buttonText: '',
  carWashName: '',
  street: '',
  city: '',
  state: '',
  zipCode: '',
  email: '',
  phoneNumber: '',
};

const DEFAULT_FILE_VALIDATION_ERRORS = { logo: '', image: '' };

@Component({
  selector: 'app-create-email-modal',
  imports: [CommonModule, FormsModule, MatRadioModule, QuillEditorComponent, EmailPreviewComponent],
  templateUrl: './create-email-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './create-email-modal.component.scss',
})
export class CreateEmailModalComponent implements OnInit {
  @Input() initialData: any = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<any>();

  constructor(
    private apiService: ApiService,
    private messageService: NotificationService,
    private cdr: ChangeDetectorRef,
    private emailPreviewService: EmailPreviewService,
    private router: Router
  ) {}

  // Form data
  emailData = {
    // Basic Info
    subject: '',
    fromEmail: '',

    // Header
    logo: null as File | null,
    logoUrl: '',

    // Body
    image: null as File | null,
    imageUrl: '',
    header: '',
    bodyText: '',
    body2Text: '',
    body3Text: '',
    body4Text: '',

    // Button
    buttonText: '',
    buttonPosition: 'bottom' as 'top' | 'bottom',
    buttonColor: '#326FD1',

    // Footer
    carWashName: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    email: '',
    phoneNumber: '',
  };

  // Loading states for file uploads
  isUploadingLogo = false;
  isUploadingImage = false;

  // Test email modal state
  showTestEmailModal = false;
  testEmailAddress = '';

  // Tab switching state
  activeTab: 'desktop' | 'mobile' = 'desktop';

  // Validation properties
  validationErrors = { ...DEFAULT_EMAIL_VALIDATION_ERRORS };

  // File validation errors
  fileValidationErrors = { ...DEFAULT_FILE_VALIDATION_ERRORS };

  // Maximum allowed dimensions (in pixels)
  maxLogoDimensions = { width: 1000, height: 1000 };
  maxImageDimensions = { width: 2000, height: 2000 };

  // Character limits
  maxSubjectLength = 200;
  maxFromEmailLength = 254;
  maxHeaderLength = 200;
  maxBodyTextLength = 5000;
  maxButtonTextLength = 50;
  maxCarWashNameLength = 100;
  maxStreetLength = 200;
  maxCityLength = 100;
  maxStateLength = 50;
  maxZipCodeLength = 10;
  maxEmailLength = 254;
  maxPhoneNumberLength = 20;

  // Color options for button
  buttonColors = [
    { value: '#326FD1', name: 'Blue' },
    { value: '#101223', name: 'Black' },
    { value: '#1DA750', name: 'Green' },
    { value: '#C79807', name: 'Yellow' },
    { value: '#059BB4', name: 'Cyan' },
    { value: '#C93D82', name: 'Pink' },
    { value: '#5457CD', name: 'Indigo' },
    { value: '#119C8D', name: 'Teal' },
    { value: '#D46213', name: 'Orange' },
    { value: '#8F48D2', name: 'Purple' },
  ];

  // Available tags for dynamic content
  availableTags = ['Customer_Name', 'Customer_Email', 'Last_Visit_Date'];

  // ngx-quill / Quill toolbar — keep parity with the previous campaign email editor
  editorModules = {
    toolbar: [[{ align: [] }], ['bold', 'italic'], [{ list: 'ordered' }, { list: 'bullet' }]],
  };

  // Track active text blocks (bodyText is always active, so we track additional ones)
  activeTextBlocks: number[] = []; // Will contain 2, 3, 4 for body2Text, body3Text, body4Text

  /**
   * Initialize component state from initialData and enable additional text blocks if corresponding body text fields are present.
   * @example
   * gOnInit()
   * void
   * @returns {void} No return value; updates this.emailData and this.activeTextBlocks based on this.initialData.
   **/
  ngOnInit(): void {
    // If initial data is provided, populate the form with it
    if (this.initialData) {
      this.emailData = { ...this.initialData };

      // In edit mode, show text blocks if they have data
      if (this.emailData.body2Text) {
        this.activeTextBlocks.push(2);
      }
      if (this.emailData.body3Text) {
        this.activeTextBlocks.push(3);
      }
      if (this.emailData.body4Text) {
        this.activeTextBlocks.push(4);
      }
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    // Validate form before saving
    if (this.validateForm()) {
      // Emit the email data to parent component
      this.save.emit(this.emailData);
      this.onClose();
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Complete email details!',
        detail: 'Fill in the empty fields to save.',
      });
    }
  }

  /**
   * Validates the current email form data on the component, populates this.validationErrors for any invalid fields, and returns whether the form is valid.
   * @example
   * validateForm()
   * true
   * @param {{void}} {{noParams}} - No arguments.
   * @returns {{boolean}} Return true if the form passes all validations, false otherwise.
   **/
  validateForm(): boolean {
    // Clear previous validation errors
    this.clearValidationErrors();

    let isValid = true;

    // Validate email subject line
    if (!this.emailData.subject || this.emailData.subject.trim() === '') {
      this.validationErrors.subject = 'Email subject line is required';
      isValid = false;
    } else if (this.emailData.subject.length > this.maxSubjectLength) {
      this.validationErrors.subject = `Email subject line cannot exceed ${this.maxSubjectLength} characters`;
      isValid = false;
    }

    // Validate from email address
    if (!this.emailData.fromEmail || this.emailData.fromEmail.trim() === '') {
      this.validationErrors.fromEmail = 'From email address is required';
      isValid = false;
    } else if (!this.isValidEmail(this.emailData.fromEmail)) {
      this.validationErrors.fromEmail = 'Please enter a valid email address';
      isValid = false;
    } else if (this.emailData.fromEmail.length > this.maxFromEmailLength) {
      this.validationErrors.fromEmail = `From email address cannot exceed ${this.maxFromEmailLength} characters`;
      isValid = false;
    }

    // Validate logo
    if (!this.emailData.logo && !this.emailData.logoUrl) {
      this.validationErrors.logo = 'Logo is required';
      isValid = false;
    }

    // Validate image
    if (!this.emailData.image && !this.emailData.imageUrl) {
      this.validationErrors.image = 'Image is required';
      isValid = false;
    }

    // Validate email header
    if (!this.emailData.header || this.emailData.header.trim() === '') {
      this.validationErrors.header = 'Email header is required';
      isValid = false;
    } else if (this.emailData.header.length > this.maxHeaderLength) {
      this.validationErrors.header = `Email header cannot exceed ${this.maxHeaderLength} characters`;
      isValid = false;
    }

    // Validate email body text (Quill empty markup like <p><br></p> counts as empty)
    const plainBodyText = this.stripHtmlTags(this.emailData.bodyText || '');
    if (!plainBodyText) {
      this.validationErrors.bodyText = 'Email body text is required';
      isValid = false;
    } else if (plainBodyText.length > this.maxBodyTextLength) {
      this.validationErrors.bodyText = `Email body text cannot exceed ${this.maxBodyTextLength} characters`;
      isValid = false;
    }

    // Validate additional body text fields if they exist
    if (this.emailData.body2Text) {
      const plainText2 = this.stripHtmlTags(this.emailData.body2Text);
      if (plainText2.length > this.maxBodyTextLength) {
        this.validationErrors.body2Text = `Body 2 text cannot exceed ${this.maxBodyTextLength} characters`;
        isValid = false;
      }
    }

    if (this.emailData.body3Text) {
      const plainText3 = this.stripHtmlTags(this.emailData.body3Text);
      if (plainText3.length > this.maxBodyTextLength) {
        this.validationErrors.body3Text = `Body 3 text cannot exceed ${this.maxBodyTextLength} characters`;
        isValid = false;
      }
    }

    if (this.emailData.body4Text) {
      const plainText4 = this.stripHtmlTags(this.emailData.body4Text);
      if (plainText4.length > this.maxBodyTextLength) {
        this.validationErrors.body4Text = `Body 4 text cannot exceed ${this.maxBodyTextLength} characters`;
        isValid = false;
      }
    }

    // Validate button text
    if (!this.emailData.buttonText || this.emailData.buttonText.trim() === '') {
      this.validationErrors.buttonText = 'Button text is required';
      isValid = false;
    } else if (this.emailData.buttonText.length > this.maxButtonTextLength) {
      this.validationErrors.buttonText = `Button text cannot exceed ${this.maxButtonTextLength} characters`;
      isValid = false;
    }

    // Validate optional footer fields for character limits
    if (
      this.emailData.carWashName &&
      this.emailData.carWashName.length > this.maxCarWashNameLength
    ) {
      this.validationErrors.carWashName = `Car Wash Name cannot exceed ${this.maxCarWashNameLength} characters`;
      isValid = false;
    }

    if (this.emailData.street && this.emailData.street.length > this.maxStreetLength) {
      this.validationErrors.street = `Street cannot exceed ${this.maxStreetLength} characters`;
      isValid = false;
    }

    if (this.emailData.city && this.emailData.city.length > this.maxCityLength) {
      this.validationErrors.city = `City cannot exceed ${this.maxCityLength} characters`;
      isValid = false;
    }

    if (this.emailData.state && this.emailData.state.length > this.maxStateLength) {
      this.validationErrors.state = `State cannot exceed ${this.maxStateLength} characters`;
      isValid = false;
    }

    if (this.emailData.zipCode && this.emailData.zipCode.length > this.maxZipCodeLength) {
      this.validationErrors.zipCode = `ZIP Code cannot exceed ${this.maxZipCodeLength} characters`;
      isValid = false;
    }

    if (this.emailData.email && this.emailData.email.length > this.maxEmailLength) {
      this.validationErrors.email = `Reply-to Email cannot exceed ${this.maxEmailLength} characters`;
      isValid = false;
    } else if (this.emailData.email && !this.isValidEmail(this.emailData.email)) {
      this.validationErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Validate phone number if provided (optional field)
    if (this.emailData.phoneNumber && this.emailData.phoneNumber.trim() !== '') {
      if (this.emailData.phoneNumber.length > this.maxPhoneNumberLength) {
        this.validationErrors.phoneNumber = `Phone Number cannot exceed ${this.maxPhoneNumberLength} characters`;
        isValid = false;
      } else {
        // Validate phone number format using reusable validator
        const control = { value: this.emailData.phoneNumber } as any;
        const phoneValidationResult = PhoneValidator.usaPhone(control);
        if (phoneValidationResult) {
          this.validationErrors.phoneNumber =
            phoneValidationResult['usaPhone']?.message || 'Please enter a valid phone number';
          isValid = false;
        }
      }
    }

    return isValid;
  }

  clearValidationErrors(): void {
    this.validationErrors = { ...DEFAULT_EMAIL_VALIDATION_ERRORS };
    this.fileValidationErrors = { ...DEFAULT_FILE_VALIDATION_ERRORS };
  }

  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Extracts visible plain text from Quill/HTML markup.
   * Treats empty editor HTML (e.g. `<p><br></p>`, `&nbsp;`) as empty after trim.
   */
  stripHtmlTags(html: string): string {
    if (!html) {
      return '';
    }
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.replace(/\u00A0/g, ' ').trim();
  }

  onSubjectChange(): void {
    // Clear validation error when user starts typing
    if (this.validationErrors.subject && this.emailData.subject) {
      const trimmedSubject = this.emailData.subject.trim();
      if (trimmedSubject !== '' && trimmedSubject.length <= this.maxSubjectLength) {
        this.validationErrors.subject = '';
      }
    }
  }

  onFromEmailChange(): void {
    // Clear validation error when user starts typing
    if (this.validationErrors.fromEmail && this.emailData.fromEmail) {
      const trimmedEmail = this.emailData.fromEmail.trim();
      if (
        trimmedEmail !== '' &&
        this.isValidEmail(trimmedEmail) &&
        trimmedEmail.length <= this.maxFromEmailLength
      ) {
        this.validationErrors.fromEmail = '';
      }
    }
  }

  onHeaderChange(): void {
    // Clear validation error when user starts typing
    if (this.validationErrors.header && this.emailData.header) {
      const trimmedHeader = this.emailData.header.trim();
      if (trimmedHeader !== '' && trimmedHeader.length <= this.maxHeaderLength) {
        this.validationErrors.header = '';
      }
    }
  }

  onButtonTextChange(): void {
    // Clear validation error when user starts typing
    if (this.validationErrors.buttonText && this.emailData.buttonText) {
      const trimmedButtonText = this.emailData.buttonText.trim();
      if (trimmedButtonText !== '' && trimmedButtonText.length <= this.maxButtonTextLength) {
        this.validationErrors.buttonText = '';
      }
    }
  }

  onBodyTextChange(): void {
    // Clear validation error only when Quill has real (non-empty) text content
    if (this.validationErrors.bodyText && this.emailData.bodyText) {
      const plainText = this.stripHtmlTags(this.emailData.bodyText);
      if (plainText !== '' && plainText.length <= this.maxBodyTextLength) {
        this.validationErrors.bodyText = '';
      }
    }
  }

  onBody2TextChange(): void {
    // Validation for body2Text is handled in validateForm if needed
  }

  onBody3TextChange(): void {
    // Validation for body3Text is handled in validateForm if needed
  }

  onBody4TextChange(): void {
    // Validation for body4Text is handled in validateForm if needed
  }

  // File validation methods
  validateFileType(file: File): boolean {
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    return allowedTypes.includes(file.type);
  }

  validateFileSize(file: File): boolean {
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    return file.size <= maxSize;
  }

  /**
   * Validate that an image file's pixel dimensions do not exceed the specified maximum width and height.
   * @example
   * alidateImageDimensions(file, 1024, 768)
   * true
   * @param {File} file - The image file to validate.
   * @param {number} maxWidth - The maximum allowed image width in pixels.
   * @param {number} maxHeight - The maximum allowed image height in pixels.
   * @returns {Promise<boolean>} Resolves to true if image dimensions are within limits, otherwise false.
   **/
  validateImageDimensions(file: File, maxWidth: number, maxHeight: number): Promise<boolean> {
    return new Promise(resolve => {
      const img = new Image();
      const url = URL.createObjectURL(file);

      img.onload = () => {
        URL.revokeObjectURL(url);
        const isValid = img.width <= maxWidth && img.height <= maxHeight;
        resolve(isValid);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(false);
      };

      img.src = url;
    });
  }

  getFileValidationError(file: File): string {
    if (!this.validateFileType(file)) {
      return 'Only PNG, JPG, and JPEG files are allowed';
    }
    if (!this.validateFileSize(file)) {
      return 'File size must be 5MB or less';
    }
    return '';
  }

  /**
   * Validate a File object's type, size, and image dimensions; returns an error message or an empty string when valid.
   * @example
   * getFileValidationErrorWithDimensions(file, 800, 600)
   * 'Image dimensions must not exceed 800x600 pixels'
   * @param {File} file - File object to validate (image file).
   * @param {number} maxWidth - Maximum allowed image width in pixels.
   * @param {number} maxHeight - Maximum allowed image height in pixels.
   * @returns {Promise<string>} Promise that resolves to an error message string if validation fails, or an empty string if the file is valid.
   **/
  async getFileValidationErrorWithDimensions(
    file: File,
    maxWidth: number,
    maxHeight: number
  ): Promise<string> {
    // First check type and size synchronously
    if (!this.validateFileType(file)) {
      return 'Only PNG, JPG, and JPEG files are allowed';
    }
    if (!this.validateFileSize(file)) {
      return 'File size must be 5MB or less';
    }

    // Then check dimensions asynchronously
    const isValidDimensions = await this.validateImageDimensions(file, maxWidth, maxHeight);
    if (!isValidDimensions) {
      return `Image dimensions must not exceed ${maxWidth}x${maxHeight} pixels`;
    }

    return '';
  }

  onSendTest(): void {
    if (!this.validateForm()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Complete email details!',
        detail: 'Fill in all the empty fields to send the test email.',
      });
      return;
    }
    this.testEmailAddress = this.emailData.fromEmail || '';
    console.log('Opening test email modal, showTestEmailModal was:', this.showTestEmailModal);
    this.showTestEmailModal = true;
    this.cdr.detectChanges(); // Force change detection
    console.log('After opening, showTestEmailModal is now:', this.showTestEmailModal);
  }

  onCloseTestEmailModal(): void {
    console.log('Closing test email modal, showTestEmailModal was:', this.showTestEmailModal);
    this.showTestEmailModal = false;
    this.testEmailAddress = '';
    this.cdr.detectChanges(); // Force change detection
    console.log('After closing, showTestEmailModal is now:', this.showTestEmailModal);
  }

  /**
   * Send a test email by building a payload from the component's emailData and sending it to testEmailAddress.
   * @example
   * nSendTestEmail()
   * // void
   * @param {string} testEmailAddress - The recipient email address from the component state (this.testEmailAddress) used to send the test email; if empty, no request is made.
   * @returns {void} Triggers an API request (this.apiService.sendTestEmail) and shows success or error notifications; does not return a value.
   */
  onSendTestEmail(): void {
    if (this.testEmailAddress) {
      const payload = {
        platform: 'email',
        email_subject: this.emailData.subject || '',
        email_from_id: this.emailData.fromEmail || '',
        email_logo_img: this.emailData.logoUrl || this.emailData.logo || '',
        email_body_img: this.emailData.imageUrl || this.emailData.image || '',
        email_header: this.emailData.header || '',
        email_body_text: this.emailData.bodyText || '',
        email_body_text2: this.emailData.body2Text || '',
        email_body_text3: this.emailData.body3Text || '',
        email_body_text4: this.emailData.body4Text || '',
        email_button_text: this.emailData.buttonText || 'Buy now',
        email_button_position: this.emailData.buttonPosition || '',
        email_button_color: this.emailData.buttonColor || '',
        email_footer_name: this.emailData.carWashName || '',
        email_footer_street: this.emailData.street || '',
        email_footer_city: this.emailData.city || '',
        email_footer_state: this.emailData.state || '',
        email_footer_zip: this.emailData.zipCode || '',
        email_reply_to: this.emailData.email || '',
        email_footer_phone: this.emailData.phoneNumber || '',
        email_test_id: this.testEmailAddress,
        recipientEmail: this.testEmailAddress,
      };

      this.apiService.sendTestEmail(payload).subscribe({
        next: () => {
          console.log('Test email sent successfully');
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Test email sent successfully',
          });
          this.onCloseTestEmailModal();
        },
        error: error => {
          console.error('Error sending test email:', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to send test email. Please try again.',
          });
        },
      });
    }
  }

  /**
   * Handle logo file selection: validate file (including dimensions), set upload state, and initiate upload if valid.
   * @example
   * onLogoUpload(event)
   * Promise<void>
   * @param {{any}} {{event}} - The file input change event containing the selected file to validate and upload.
   * @returns {{Promise<void>}} Promise that resolves when processing (validation and upload initiation) is complete.
   **/
  async onLogoUpload(event: any): Promise<void> {
    const file = event.target.files[0];
    if (file) {
      // Clear previous validation errors
      this.fileValidationErrors.logo = '';
      this.validationErrors.logo = '';
      this.cdr.detectChanges();

      // Validate file (including dimensions)
      const validationError = await this.getFileValidationErrorWithDimensions(
        file,
        this.maxLogoDimensions.width,
        this.maxLogoDimensions.height
      );
      if (validationError) {
        this.fileValidationErrors.logo = validationError;
        // Clear the file input
        event.target.value = '';
        this.cdr.detectChanges();
        return;
      }

      // Set loading state and file before upload starts
      this.isUploadingLogo = true;
      this.emailData.logo = file;
      this.cdr.detectChanges();
      this.uploadLogo(file);
    }
  }

  /**
   * Handle file input change to validate selected image (including dimensions), set upload state, and start upload.
   * @example
   * onImageUpload({ target: { files: [file] } })
   * Promise<void>
   * @param {{any}} {{event}} - File input change event (input[type="file"] change event) containing event.target.files.
   * @returns {{Promise<void>}} Promise that resolves when processing completes (no return value).
   **/
  async onImageUpload(event: any): Promise<void> {
    const [file] = event.target.files || [];
    if (file) {
      // Clear previous validation errors
      this.fileValidationErrors.image = '';
      this.validationErrors.image = '';
      this.cdr.detectChanges();

      // Validate file (including dimensions)
      const validationError = await this.getFileValidationErrorWithDimensions(
        file,
        this.maxImageDimensions.width,
        this.maxImageDimensions.height
      );
      if (validationError) {
        this.fileValidationErrors.image = validationError;
        // Clear the file input
        event.target.value = '';
        this.cdr.detectChanges();
        return;
      }

      // Set loading state and file before upload starts
      this.isUploadingImage = true;
      this.emailData.image = file;
      this.cdr.detectChanges();
      this.uploadImage(file);
    }
  }

  /**
   * Uploads a logo File to the server, updates component state with the returned logo URL, clears validation errors, and manages upload state and change detection.
   * @example
   * uploadLogo(myLogoFile)
   * void
   * @param {{File}} {{file}} - File object for the logo image to upload.
   * @returns {{void}} No return value; the function performs side effects (updates emailData, validation errors, and UI state).
   **/
  private uploadLogo(file: File): void {
    const formData = new FormData();
    formData.append('email_logo_img', file);

    this.apiService.uploadCampaignFile(formData).subscribe({
      next: response => {
        if (response.status && response.data && response.data.email_logo_img) {
          this.emailData.logoUrl = response.data.email_logo_img;
          // Clear validation errors on successful upload
          this.validationErrors.logo = '';
          this.fileValidationErrors.logo = '';
          console.log('Logo uploaded successfully:', this.emailData.logoUrl);
        } else {
          console.error('Logo upload failed: Invalid response structure');
          this.emailData.logo = null;
        }
        this.isUploadingLogo = false;
        this.cdr.detectChanges();
      },
      error: error => {
        this.isUploadingLogo = false;
        console.error('Error uploading logo:', error);
        // Reset logo on error
        this.emailData.logo = null;
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Uploads an image File to the campaign API and updates component state on success or error.
   * @example
   * uploadImage(sampleFile)
   * void
   * @param {{File}} {{file}} - The image File to upload.
   * @returns {{void}} No return value; updates component state asynchronously (sets imageUrl, clears validation errors, and handles upload state and errors).
   **/
  private uploadImage(file: File): void {
    const formData = new FormData();
    formData.append('email_body_img', file);

    this.apiService.uploadCampaignFile(formData).subscribe({
      next: response => {
        if (response.status && response.data && response.data.email_body_img) {
          this.emailData.imageUrl = response.data.email_body_img;
          // Clear validation errors on successful upload
          this.validationErrors.image = '';
          this.fileValidationErrors.image = '';
          console.log('Image uploaded successfully:', this.emailData.imageUrl);
        } else {
          console.error('Image upload failed: Invalid response structure');
          this.emailData.image = null;
        }
        this.isUploadingImage = false;
        this.cdr.detectChanges();
      },
      error: error => {
        this.isUploadingImage = false;
        console.error('Error uploading image:', error);
        // Reset image on error
        this.emailData.image = null;
        this.cdr.detectChanges();
      },
    });
  }

  removeLogo(): void {
    this.emailData.logo = null;
    this.emailData.logoUrl = '';
    this.fileValidationErrors.logo = '';
  }

  removeImage(): void {
    this.emailData.image = null;
    this.emailData.imageUrl = '';
    this.fileValidationErrors.image = '';
  }

  /**
   * Adds a new email body text block (up to three additional blocks for a total of four) by finding the next available block number (2–4) and initializing its corresponding emailData property if missing.
   * @example
   * ddTextBlock()
   * undefined
   * @returns {void} No return value.
   **/
  addTextBlock(): void {
    // Add new text block up to 3 additional blocks (body2Text, body3Text, body4Text)
    // Total blocks: bodyText (always) + up to 3 additional = 4 total

    // Find the next available block number (2, 3, or 4)
    for (let i = 2; i <= 4; i += 1) {
      if (!this.activeTextBlocks.includes(i)) {
        this.activeTextBlocks.push(i);
        // Initialize the text block if it doesn't exist
        if (i === 2 && !this.emailData.body2Text) {
          this.emailData.body2Text = '';
        } else if (i === 3 && !this.emailData.body3Text) {
          this.emailData.body3Text = '';
        } else if (i === 4 && !this.emailData.body4Text) {
          this.emailData.body4Text = '';
        }
        break; // Only add one block at a time
      }
    }
  }

  removeBody2(): void {
    this.emailData.body2Text = '';
    this.activeTextBlocks = this.activeTextBlocks.filter(block => block !== 2);
  }

  removeBody3(): void {
    this.emailData.body3Text = '';
    this.activeTextBlocks = this.activeTextBlocks.filter(block => block !== 3);
  }

  removeBody4(): void {
    this.emailData.body4Text = '';
    this.activeTextBlocks = this.activeTextBlocks.filter(block => block !== 4);
  }

  canAddTextBlock(): boolean {
    // Can add if we have less than 3 additional blocks (body2Text, body3Text, body4Text)
    return this.activeTextBlocks.length < 3;
  }

  selectButtonColor(color: string): void {
    this.emailData.buttonColor = color;
  }

  /**
   * Inserts a placeholder tag (wrapped in double curly braces) into a chosen email field and updates validation state.
   * @example
   * nsertTag('FIRST_NAME', 'bodyText')
   * undefined
   * @param {{string}} {{tag}} - Tag key (without braces) to insert into the target field.
   * @param {{string}} {{targetField}} - Target emailData field to append the tag to ('header', 'bodyText', 'body2Text', 'body3Text', 'body4Text').
   * @returns {{void}} Mutates emailData by appending the tag and may clear related validation errors; does not return a value.
   **/
  insertTag(tag: string, targetField: string): void {
    const tagWithBrackets = `{{${tag}}}`;

    switch (targetField) {
      case 'header':
        this.emailData.header += tagWithBrackets;
        // Clear validation error if header becomes valid
        if (
          this.validationErrors.header &&
          this.emailData.header.trim() !== '' &&
          this.emailData.header.length <= this.maxHeaderLength
        ) {
          this.validationErrors.header = '';
        }
        break;
      case 'bodyText':
        this.emailData.bodyText += tagWithBrackets;
        // Clear validation error if bodyText becomes valid
        if (this.validationErrors.bodyText && this.emailData.bodyText) {
          const plainText = this.stripHtmlTags(this.emailData.bodyText);
          if (plainText !== '' && plainText.length <= this.maxBodyTextLength) {
            this.validationErrors.bodyText = '';
          }
        }
        break;
      case 'body2Text':
        this.emailData.body2Text += tagWithBrackets;
        break;
      case 'body3Text':
        this.emailData.body3Text += tagWithBrackets;
        break;
      case 'body4Text':
        this.emailData.body4Text += tagWithBrackets;
        break;
      default:
        console.warn('Unknown target field:', targetField);
    }
  }

  switchTab(tab: 'desktop' | 'mobile'): void {
    this.activeTab = tab;
  }

  onViewInBrowser(): void {
    // Store email data in service
    this.emailPreviewService.setEmailData(this.emailData);

    // Get the current URL and construct the new route
    const url = this.router.serializeUrl(this.router.createUrlTree(['/email-preview']));

    // Open in new tab
    window.open(url, '_blank');
  }

  /**
   * Handle changes to the phone number input: clears validation when empty, enforces maximum length, and validates US phone format in real-time.
   * @example
   * nPhoneNumberChange()
   * undefined
   * @returns {void} No return value; updates component validation state synchronously.
   **/
  onPhoneNumberChange(): void {
    // Clear validation error when user starts typing or clears the field
    if (!this.emailData.phoneNumber || this.emailData.phoneNumber.trim() === '') {
      this.validationErrors.phoneNumber = '';
      return;
    }

    // Validate phone number format in real-time
    if (this.emailData.phoneNumber.length > this.maxPhoneNumberLength) {
      this.validationErrors.phoneNumber = `Phone Number cannot exceed ${this.maxPhoneNumberLength} characters`;
      return;
    }

    const control = { value: this.emailData.phoneNumber } as any;
    const phoneValidationResult = PhoneValidator.usaPhone(control);
    if (phoneValidationResult) {
      this.validationErrors.phoneNumber =
        phoneValidationResult['usaPhone']?.message || 'Please enter a valid phone number';
    } else {
      // Clear error if valid
      this.validationErrors.phoneNumber = '';
    }
  }

  /**
   * Removes disallowed characters from an input's value based on the field type and updates the DOM input and this.emailData accordingly.
   * @example
   * ilterSpecialChars({ target: { value: "123 Main St.!@" } }, 'street')
   * undefined
   * @param {{any}} {{event}} - The input DOM event containing target.value to be filtered.
   * @param {{string}} {{fieldName}} - The field name determining allowed characters ('carWashName','street','city','state','zipCode').
   * @returns {{void}} Does not return a value; updates input.value and the corresponding property on this.emailData when changes are made.
   **/
  filterSpecialChars(event: any, fieldName: string): void {
    const input = event.target;
    const { value } = input;
    let filteredValue = '';

    switch (fieldName) {
      case 'carWashName':
        // Allow alphanumeric, spaces, hyphens, apostrophes, periods
        filteredValue = value.replace(/[^a-zA-Z0-9\s\-'.]/g, '');
        break;
      case 'street':
        // Allow alphanumeric, spaces, hyphens, periods, commas, forward slashes
        filteredValue = value.replace(/[^a-zA-Z0-9\s\-.,/]/g, '');
        break;
      case 'city':
        // Allow letters, spaces, hyphens, apostrophes
        filteredValue = value.replace(/[^a-zA-Z\s\-']/g, '');
        break;
      case 'state':
        // Allow only letters and spaces
        filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
        break;
      case 'zipCode':
        // Allow only numbers and hyphens (for ZIP+4 format)
        filteredValue = value.replace(/[^0-9-]/g, '');
        break;
      default:
        filteredValue = value;
    }

    // Update the model value
    if (filteredValue !== value) {
      input.value = filteredValue;
      switch (fieldName) {
        case 'carWashName':
          this.emailData.carWashName = filteredValue;
          break;
        case 'street':
          this.emailData.street = filteredValue;
          break;
        case 'city':
          this.emailData.city = filteredValue;
          break;
        case 'state':
          this.emailData.state = filteredValue;
          break;
        case 'zipCode':
          this.emailData.zipCode = filteredValue;
          break;
      }
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
